/**
 * Layout glyphs along a circular arc (shared math for editor + SVG render).
 * Units are whatever the caller uses (mm in design docs, px on canvas after scale).
 */

export type CurveKind = 'arc-up' | 'arc-down';

export type CurveGlyph = {
  char: string;
  /** Glyph center X relative to bounding-box top-left */
  x: number;
  /** Glyph center Y relative to bounding-box top-left */
  y: number;
  /** Rotation in degrees (clockwise, Konva/SVG-compatible) */
  rotation: number;
};

export type CurveLayout = {
  glyphs: CurveGlyph[];
  width: number;
  height: number;
  /** Circle center relative to bounding-box top-left */
  pathCx: number;
  pathCy: number;
  radius: number;
};

function estimateCharWidth(ch: string, fontSize: number): number {
  if (ch === ' ') return fontSize * 0.33;
  return fontSize * 0.56;
}

/**
 * @param measureChar optional precise width (canvas). Falls back to an estimate.
 */
export function layoutCurvedText(opts: {
  content: string;
  fontSize: number;
  curve: CurveKind;
  curveRadius: number;
  measureChar?: (ch: string) => number;
}): CurveLayout {
  const fontSize = Math.max(2, opts.fontSize);
  const raw = (opts.content || ' ').replace(/\n/g, ' ');
  const chars = [...raw];
  const measure = opts.measureChar ?? ((ch: string) => estimateCharWidth(ch, fontSize));
  const widths = chars.map((ch) => Math.max(fontSize * 0.05, measure(ch)));
  const total = widths.reduce((a, b) => a + b, 0) || fontSize;

  // Respect the user radius (smaller = tighter curve). Only enforce a tiny floor.
  const radius = Math.max(opts.curveRadius, fontSize * 0.3);
  const span = total / radius;

  const midAngle = opts.curve === 'arc-up' ? -Math.PI / 2 : Math.PI / 2;
  const circleCx = 0;
  const circleCy = 0;

  const rawGlyphs: Array<CurveGlyph & { w: number }> = [];
  let walked = 0;
  for (let i = 0; i < chars.length; i++) {
    const w = widths[i]!;
    const mid = walked + w / 2;
    let angle: number;
    let rotationRad: number;
    if (opts.curve === 'arc-up') {
      angle = midAngle - span / 2 + mid / radius;
      rotationRad = angle + Math.PI / 2;
    } else {
      angle = midAngle + span / 2 - mid / radius;
      rotationRad = angle - Math.PI / 2;
    }
    rawGlyphs.push({
      char: chars[i]!,
      x: circleCx + radius * Math.cos(angle),
      y: circleCy + radius * Math.sin(angle),
      rotation: (rotationRad * 180) / Math.PI,
      w,
    });
    walked += w;
  }

  const pad = fontSize * 0.55;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const g of rawGlyphs) {
    minX = Math.min(minX, g.x - pad);
    minY = Math.min(minY, g.y - pad);
    maxX = Math.max(maxX, g.x + pad);
    maxY = Math.max(maxY, g.y + pad);
  }
  if (!Number.isFinite(minX)) {
    minX = 0;
    minY = 0;
    maxX = fontSize;
    maxY = fontSize;
  }

  const width = Math.max(fontSize, maxX - minX);
  const height = Math.max(fontSize, maxY - minY);

  return {
    glyphs: rawGlyphs.map(({ char, x, y, rotation }) => ({
      char,
      x: x - minX,
      y: y - minY,
      rotation,
    })),
    width,
    height,
    pathCx: circleCx - minX,
    pathCy: circleCy - minY,
    radius,
  };
}

/** Suggested starting radius when enabling curve (not a hard minimum afterward). */
export function defaultCurveRadiusMm(textWidthMm: number, fontSizeMm: number): number {
  // ~100° of arc — readable default, user can tighten with the slider.
  const forSpan = textWidthMm / (Math.PI * 0.55);
  return Math.max(fontSizeMm * 1.2, Math.min(280, forSpan));
}
